import { type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { ListSelection, TranslatableString } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Input, Select, Checkbox } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { SortableList } from '@/components/ui/SortableList.js';
import { cn } from '@/lib/cn.js';

export function emptyTranslatable(): TranslatableString {
  return { uz: '', ru: '', en: '' };
}

/** Forma bo'limi — bir necha maydonni sarlavha ostida guruhlaydi. */
export function FieldGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      {title && (
        <h3 className="text-xs font-semibold tracking-wide text-n-600 uppercase">{title}</h3>
      )}
      {children}
    </div>
  );
}

export function NumberSelect({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  options: readonly number[];
  onChange: (value: number) => void;
  hint?: string;
}) {
  return (
    <Select
      label={label}
      hint={hint}
      value={String(value)}
      options={options.map((option) => ({ value: String(option), label: String(option) }))}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  );
}

export function EnumSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  hint?: string;
}) {
  return (
    <Select
      label={label}
      hint={hint}
      value={value}
      options={options}
      onChange={(event) => onChange(event.target.value as T)}
    />
  );
}

export type RepeaterProps<T> = {
  label: string;
  items: readonly T[];
  onChange: (items: T[]) => void;
  /** Yangi element — bo'sh qiymatlar bilan. */
  create: () => T;
  /** Surish uchun barqaror kalit. Indeks YARAMAYDI: surilganda React
   *  komponentlarni chalkashtirib, ochiq maydon boshqa elementga tegardi. */
  getId: (item: T, index: number) => string;
  renderItem: (item: T, update: (next: T) => void, index: number) => ReactNode;
  /** Sarlavha o'rniga qatorda ko'rinadigan matn. */
  summary?: (item: T, index: number) => string;
  max?: number;
  addLabel?: string;
  emptyHint?: string;
};

/**
 * Takrorlanuvchi elementlar ro'yxati (slaydlar, savol-javob, raqamlar).
 *
 * 31 blokning 12 tasida shunday ro'yxat bor. Har birida qayta yozilsa,
 * birortasida surish yoki chegara tekshiruvi tushib qolardi.
 */
export function Repeater<T>({
  label,
  items,
  onChange,
  create,
  getId,
  renderItem,
  summary,
  max = 40,
  addLabel = 'Qo‘shish',
  emptyHint,
}: RepeaterProps<T>) {
  function update(index: number, next: T): void {
    onChange(items.map((item, i) => (i === index ? next : item)));
  }

  function remove(index: number): void {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-n-800">
          {label}
          <span className="ml-1.5 text-xs font-normal text-n-400">
            {items.length}/{max}
          </span>
        </span>
        <Button
          size="sm"
          variant="outline"
          icon={<Plus className="size-4" />}
          disabled={items.length >= max}
          onClick={() => onChange([...items, create()])}
        >
          {addLabel}
        </Button>
      </div>

      {items.length === 0 ? (
        <p className=" border border-dashed border-n-200 px-3 py-6 text-center text-sm text-n-600">
          {emptyHint ?? 'Hozircha bo‘sh. «Qo‘shish» ni bosing.'}
        </p>
      ) : (
        <SortableList
          items={items as T[]}
          getId={getId}
          onReorder={onChange}
          renderRow={(item, index) => (
            <details className="flex-1 border border-n-200">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="truncate text-n-800">
                  {summary?.(item, index) || `${index + 1}-element`}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="O‘chirish"
                  onClick={(event) => {
                    event.preventDefault();
                    remove(index);
                  }}
                >
                  <Trash2 className="size-4 text-error-600" aria-hidden />
                </Button>
              </summary>
              <div className="space-y-3 border-t border-n-200 px-3 py-3">
                {renderItem(item, (next) => update(index, next), index)}
              </div>
            </details>
          )}
        />
      )}
    </div>
  );
}

/** Ro'yxat bloklarining sarlavha qismi — 9 ta blokda bir xil. */
export type ListHeaderValue = {
  title: TranslatableString;
  subtitle: TranslatableString;
  showMoreButton: boolean;
  moreButtonLabel: TranslatableString;
  moreButtonHref: string;
};

export function ListHeaderFields({
  value,
  onChange,
}: {
  value: ListHeaderValue;
  onChange: (patch: Partial<ListHeaderValue>) => void;
}) {
  return (
    <FieldGroup title="Sarlavha">
      <TranslatableInput
        label="Sarlavha"
        value={value.title}
        onChange={(next) => onChange({ title: next })}
      />
      <TranslatableInput
        label="Tavsif"
        multiline
        rows={2}
        value={value.subtitle}
        onChange={(next) => onChange({ subtitle: next })}
      />
      <Checkbox
        label="«Barchasi» tugmasi ko‘rsatilsin"
        checked={value.showMoreButton}
        onChange={(event) => onChange({ showMoreButton: event.target.checked })}
      />
      {value.showMoreButton && (
        <div className="space-y-3 border-l-2 border-n-200 pl-3">
          <TranslatableInput
            label="Tugma matni"
            value={value.moreButtonLabel}
            onChange={(next) => onChange({ moreButtonLabel: next })}
          />
          <Input
            label="Tugma havolasi"
            value={value.moreButtonHref}
            placeholder="/yangiliklar"
            onChange={(event) => onChange({ moreButtonHref: event.target.value })}
          />
        </div>
      )}
    </FieldGroup>
  );
}

const MODE_LABELS: Record<ListSelection['mode'], string> = {
  auto: 'Avtomatik — eng yangilari',
  featured: 'Faqat tanlanganlar (yulduzchali)',
  category: 'Kategoriya bo‘yicha',
  brand: 'Brend bo‘yicha',
  manual: 'Qo‘lda tanlash',
};

export type SelectionFieldsProps = {
  value: ListSelection;
  onChange: (value: ListSelection) => void;
  /** Qaysi rejimlar mavjud — blokka qarab farq qiladi. */
  modes?: ReadonlyArray<ListSelection['mode']>;
  /** `category`/`brand` rejimida tanlanadigan ro'yxat. */
  targets?: ReadonlyArray<{ value: string; label: string }>;
  targetLabel?: string;
  /** `manual` rejimida tanlanadigan elementlar. */
  items?: ReadonlyArray<{ value: string; label: string }>;
  itemsLabel?: string;
};

/**
 * Ro'yxat bloki nimani ko'rsatishini tanlash.
 *
 * `auto` rejimida kontent SERVER tomonda to'ldiriladi — admin faqat
 * nechta ko'rsatilishini aytadi. `manual` da esa tartib ham saqlanadi
 * (`itemIds` ketma-ketligi).
 */
export function SelectionFields({
  value,
  onChange,
  modes = ['auto', 'featured', 'manual'],
  targets,
  targetLabel = 'Kategoriya',
  items,
  itemsLabel = 'Elementlar',
}: SelectionFieldsProps) {
  function set(patch: Partial<ListSelection>): void {
    onChange({ ...value, ...patch });
  }

  return (
    <FieldGroup title="Nima ko‘rsatiladi">
      <Select
        label="Tanlash usuli"
        value={value.mode}
        options={modes.map((mode) => ({ value: mode, label: MODE_LABELS[mode] }))}
        onChange={(event) => set({ mode: event.target.value as ListSelection['mode'] })}
      />

      {(value.mode === 'category' || value.mode === 'brand') && (
        <Select
          label={targetLabel}
          value={value.targetId ?? ''}
          placeholder="Tanlanmagan"
          options={targets ?? []}
          onChange={(event) => set({ targetId: event.target.value || null })}
        />
      )}

      {value.mode === 'manual' && (
        <MultiSelect
          label={itemsLabel}
          selected={value.itemIds}
          options={items ?? []}
          onChange={(next) => set({ itemIds: next })}
          hint="Tanlangan tartib saytda ham saqlanadi"
        />
      )}

      {value.mode !== 'manual' && (
        <Input
          type="number"
          label="Nechta ko‘rsatilsin"
          min={1}
          max={40}
          value={value.limit}
          onChange={(event) => set({ limit: Number(event.target.value) || 1 })}
        />
      )}
    </FieldGroup>
  );
}

export function MultiSelect({
  label,
  selected,
  options,
  onChange,
  hint,
  max = 40,
}: {
  label: string;
  selected: readonly string[];
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
  hint?: string;
  max?: number;
}) {
  const chosen = selected
    .map((id) => options.find((option) => option.value === id))
    .filter((option): option is { value: string; label: string } => option !== undefined);

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium text-n-800">{label}</span>

      {chosen.length > 0 && (
        <SortableList
          items={chosen}
          getId={(option) => option.value}
          onReorder={(next) => onChange(next.map((option) => option.value))}
          renderRow={(option) => (
            <div className="flex flex-1 items-center justify-between gap-2">
              <span className="truncate text-sm text-n-800">{option.label}</span>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Olib tashlash"
                onClick={() => onChange(selected.filter((id) => id !== option.value))}
              >
                <Trash2 className="size-4 text-error-600" aria-hidden />
              </Button>
            </div>
          )}
        />
      )}

      <Select
        aria-label={`${label} — qo‘shish`}
        value=""
        placeholder={selected.length >= max ? 'Chegaraga yetildi' : 'Qo‘shish…'}
        disabled={selected.length >= max}
        options={options.filter((option) => !selected.includes(option.value))}
        onChange={(event) => {
          if (event.target.value) onChange([...selected, event.target.value]);
        }}
      />

      {hint && <p className="text-sm text-n-600">{hint}</p>}
    </div>
  );
}

/** Ikon nomi — sayt tomonda `lucide-react` dan olinadi. */
export function IconInput({
  value,
  onChange,
  label = 'Belgi (ikon)',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return (
    <Input
      label={label}
      value={value}
      placeholder="map-pin"
      onChange={(event) => onChange(event.target.value)}
      hint="lucide.dev saytidagi belgi nomi. Bo‘sh qoldirilsa belgi ko‘rsatilmaydi"
    />
  );
}

/** Ustunlar sonini tanlash — vizual tugmalar bilan. */
export function ColumnsPicker({
  value,
  options,
  onChange,
}: {
  value: number;
  options: readonly number[];
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium text-n-800">Ustunlar soni</span>
      <div className="flex gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={cn(
              'flex h-10 flex-1 items-center justify-center gap-0.5 border text-sm',
              value === option
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-n-200 bg-white text-n-600 hover:bg-n-50',
            )}
          >
            {Array.from({ length: option }, (_, index) => (
              <span key={index} className="h-4 w-1.5 bg-current opacity-60" />
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}
